import { FormFieldController } from 'components/form/FormFieldController'
import { convertToMetricBaseUnit } from 'components/form/formHelpers'
import { FormItem } from 'components/form/FormItem'
import { SimpleFormField } from 'components/form/formTypes'
import { AddressInput } from 'components/form/inputs/AddressInput'
import { CoordinateInputWithLabel } from 'components/form/inputs/coordinatesInput/CoordinateInputWithLabel'
import {
  convertPossibleFieldValuesToCoordinates,
  CoordinateInputTablePointAndClickApi,
} from 'components/form/inputs/coordinatesInput/CoordinatesInputTable'
import {
  Coordinate,
  isFullyDefinedCoordinate,
  isFullyDefinedCoordinateArray,
  serializeCoordinates,
} from 'components/form/inputs/coordinatesInput/helpers/coordinateTranslations'
import { DateInput } from 'components/form/inputs/DateInput'
import { Dropdown } from 'components/form/inputs/Dropdown'
import { MultiSelect } from 'components/form/inputs/MultiSelect'
import { NumberInput } from 'components/form/inputs/NumberInput'
import { TextInput } from 'components/form/inputs/TextInput'
// eslint-disable-next-line boundaries/dependencies
import { useGeckoLocale } from 'features/locale'
import React, { useMemo } from 'react'
import {
  Control,
  FieldErrors,
  UseFormSetValue,
  UseFormTrigger,
} from 'react-hook-form'
import styled from 'styled-components'
import { useGeckoTheme } from 'styles/GeckoTheme'
import {
  convertCoordinateString,
  FormField,
  GeckoMeasurement,
  GeckoMeasurementUnit,
  InputType,
} from 'types'

type RenderFormFieldProps = {
  formItemKey: string
  formItem: FormField
  fieldValue: string | number | string[] | undefined
  fieldUnits?: string
  formControl: Control<SimpleFormField>
  formSetValue: UseFormSetValue<{
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- this is the actual type
    [x: string]: any
  }>
  formErrors: FieldErrors<SimpleFormField>
  formTrigger: UseFormTrigger<SimpleFormField>
  borderColor?: string
  labelColor?: string
  units: GeckoMeasurementUnit | null
  fieldMode: 'edit' | 'read'
  formItemLabel: string | undefined
  ariaLabelledBy?: string
  pointAndClickApi: CoordinateInputTablePointAndClickApi | undefined
  displayCoordinateUpdates: (newCoords: ReadonlyArray<Coordinate>) => void
  highlightCoordinate: (coordinate: Coordinate | null) => void
}

const StyledFormItem = styled(FormItem)`
  display: contents;
  border-radius: 6px;
`

export const RenderFormField: React.FC<RenderFormFieldProps> = ({
  formItemKey,
  formItem,
  fieldValue,
  fieldUnits,
  formControl,
  formSetValue,
  formErrors,
  formTrigger,
  borderColor,
  labelColor,
  fieldMode,
  formItemLabel,
  units,
  pointAndClickApi,
  displayCoordinateUpdates,
  highlightCoordinate,
  ariaLabelledBy,
}) => {
  const { theme } = useGeckoTheme()
  const { getPreferredMeasurementUnit } = useGeckoLocale()

  const labelledByProps = ariaLabelledBy
    ? { 'aria-labelledby': ariaLabelledBy }
    : {}

  const definedPointAndClickApi = useMemo(
    () =>
      pointAndClickApi ??
      ({
        isInPointSelectionMode: false,
        enterPointSelectionMode: null,
      } satisfies CoordinateInputTablePointAndClickApi),
    [pointAndClickApi]
  )

  return (
    <>
      <FormFieldController
        control={formControl}
        name={formItemKey}
        render={({ field: { ...fieldProps } }) => {
          const inputType = formItem.inputType
          switch (inputType) {
            case InputType.Text:
            case InputType.TextLink:
            case InputType.TextArea:
              return (
                <StyledFormItem>
                  <TextInput
                    data-testid={`custom-form-field-${formItemLabel}`}
                    defaultValue={fieldValue ?? ''}
                    $subtitleText={formErrors[fieldProps.name]?.message}
                    $subtitleTextColor={theme.forecast.dangerText}
                    onChange={(e) => {
                      fieldProps.onChange(e.target.value)
                      formTrigger(fieldProps.name)
                    }}
                    $borderColor={borderColor}
                    $labelColor={labelColor}
                    $label={formItemLabel}
                    $labelLocation={
                      formItemLabel !== undefined ? 'side' : undefined
                    }
                    mode={fieldMode}
                    disabled={formItem.disabled}
                    type={
                      inputType === InputType.TextLink
                        ? 'link'
                        : inputType === InputType.TextArea
                          ? 'textarea'
                          : 'input'
                    }
                    {...labelledByProps}
                  />
                </StyledFormItem>
              )
            case InputType.Number:
              return (
                <NumberInput
                  defaultValue={fieldValue}
                  data-testid={`custom-form-field-${formItemLabel}`}
                  $subtitleText={formErrors[fieldProps.name]?.message}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const input = parseFloat(e.target.value)
                    //all gecko measurement data is store in metric
                    const storedUnit = convertToMetricBaseUnit(
                      formItem.unit_magnitude
                    )
                    if (!Number.isNaN(input) && units) {
                      const convertedValue = GeckoMeasurement.CreateFromValue(
                        input,
                        units
                      ).convertTo(storedUnit ?? GeckoMeasurementUnit.Meters).num
                      fieldProps.onChange(convertedValue)
                    } else {
                      fieldProps.onChange(e.target.value)
                    }
                    formTrigger(fieldProps.name)
                  }}
                  $borderColor={borderColor}
                  $subtitleTextColor={theme.forecast.dangerText}
                  $labelColor={labelColor}
                  $label={formItemLabel}
                  $labelLocation={
                    formItemLabel !== undefined ? 'side' : undefined
                  }
                  units={fieldUnits}
                  mode={fieldMode}
                  disabled={formItem.disabled}
                  {...labelledByProps}
                />
              )
            case InputType.Dropdown:
              return (
                <Dropdown
                  data-testid={`custom-form-field-${formItemLabel}`}
                  defaultValue={fieldValue?.toString()}
                  options={formItem.options ?? []}
                  $subtitleText={formErrors[fieldProps.name]?.message}
                  onValueChange={(e) => {
                    fieldProps.onChange(e)
                    formTrigger(fieldProps.name)
                  }}
                  $borderColor={borderColor}
                  $subtitleTextColor={theme.forecast.dangerText}
                  $label={formItemLabel}
                  $labelColor={labelColor}
                  $labelLocation={
                    formItemLabel !== undefined ? 'side' : undefined
                  }
                  $itemKey={formItemKey}
                  $ariaLabelledBy={ariaLabelledBy}
                  mode={fieldMode}
                  disabled={formItem.disabled}
                  placeholder='Select an option'
                  renderMenuItem={formItem.renderMenuItem}
                />
              )
            case InputType.Coordinates: {
              const unitToUse =
                units ??
                getPreferredMeasurementUnit(GeckoMeasurementUnit.Meters)
              const valueAsGeckoCoordinates =
                convertPossibleFieldValuesToCoordinates(fieldValue, unitToUse)

              return (
                <CoordinateInputWithLabel
                  coordinates={valueAsGeckoCoordinates}
                  $subtitleText={formErrors[fieldProps.name]?.message}
                  $subtitleTextColor={theme.forecast.dangerText}
                  $label={formItemLabel}
                  $labelColor={labelColor}
                  isReadonly={fieldMode === 'read'}
                  disabled={formItem.disabled}
                  pointAndClickApi={definedPointAndClickApi}
                  highlightCoordinate={highlightCoordinate}
                  onChange={(coordinateUpdates) => {
                    if (isFullyDefinedCoordinateArray(coordinateUpdates)) {
                      const serializedCoordinates =
                        serializeCoordinates(coordinateUpdates)
                      const valueInMeters = convertCoordinateString(
                        serializedCoordinates,
                        unitToUse,
                        GeckoMeasurementUnit.Meters
                      )
                      fieldProps.onChange(valueInMeters)
                      displayCoordinateUpdates(coordinateUpdates)
                    } else {
                      displayCoordinateUpdates(
                        coordinateUpdates.filter(isFullyDefinedCoordinate)
                      )
                      fieldProps.onChange(null)
                    }
                    formTrigger(fieldProps.name)
                  }}
                />
              )
            }
            case InputType.Date:
              return (
                <DateInput
                  data-testid={`custom-form-field-${formItemLabel}`}
                  defaultValue={fieldValue}
                  $subtitleText={formErrors[fieldProps.name]?.message}
                  onChange={(e) => {
                    fieldProps.onChange(e.target.value)
                    formTrigger(fieldProps.name)
                  }}
                  $borderColor={borderColor}
                  $subtitleTextColor={theme.forecast.dangerText}
                  $label={formItemLabel}
                  $labelColor={labelColor}
                  $labelLocation={
                    formItemLabel !== undefined ? 'side' : undefined
                  }
                  mode={fieldMode}
                  disabled={formItem.disabled}
                  {...labelledByProps}
                />
              )
            case InputType.Address:
              return (
                <AddressInput
                  data-testid={`custom-form-field-${fieldProps.name}`}
                  defaultValue={fieldValue}
                  onChange={(e) => {
                    fieldProps.onChange(e.target.value)
                    formTrigger(fieldProps.name)
                  }}
                  formItemKey={formItemKey}
                  formSetValue={formSetValue}
                  $borderColor={borderColor}
                  $label={formItemLabel}
                  $labelColor={labelColor}
                  $labelLocation={
                    formItemLabel !== undefined ? 'side' : undefined
                  }
                  mode={fieldMode}
                  disabled={formItem.disabled}
                  formatAddressValueAsString={
                    formItem.formatAddressValueAsString
                  }
                  {...labelledByProps}
                />
              )
            case InputType.MultiSelect:
              return (
                <MultiSelect
                  data-testid={`custom-form-field-${formItemLabel}`}
                  defaultValue={fieldValue}
                  options={formItem.options ?? []}
                  $subtitleText={formErrors[fieldProps.name]?.message}
                  onValueChange={(e) => {
                    fieldProps.onChange(e)
                    formTrigger(fieldProps.name)
                  }}
                  $borderColor={borderColor}
                  $subtitleTextColor={theme.forecast.dangerText}
                  $label={formItemLabel}
                  $labelColor={labelColor}
                  $itemKey={formItemKey}
                  $ariaLabelledBy={ariaLabelledBy}
                  $labelLocation={
                    formItemLabel !== undefined ? 'side' : undefined
                  }
                  mode={fieldMode}
                  placeholder='Select options'
                  disabled={formItem.disabled}
                />
              )
            case InputType.Combobox:
              return (
                <Dropdown
                  data-testid={`custom-form-field-${formItemLabel}`}
                  defaultValue={
                    typeof fieldValue === 'string' ? fieldValue : undefined
                  }
                  options={formItem.options ?? []}
                  $subtitleText={formErrors[fieldProps.name]?.message}
                  onValueChange={(value: string) => {
                    fieldProps.onChange(value)
                    formTrigger(fieldProps.name)
                  }}
                  $borderColor={borderColor}
                  $subtitleTextColor={theme.forecast.dangerText}
                  $label={formItemLabel}
                  $labelColor={labelColor}
                  $itemKey={formItemKey}
                  $ariaLabelledBy={ariaLabelledBy}
                  $labelLocation={
                    formItemLabel !== undefined ? 'side' : undefined
                  }
                  mode={fieldMode}
                  placeholder='Select an option'
                  disabled={formItem.disabled}
                />
              )
            default:
              return (
                <StyledFormItem>
                  <TextInput
                    data-testid={`custom-form-field-${formItemLabel}`}
                    defaultValue={fieldValue ?? ''}
                    $subtitleText={formErrors[fieldProps.name]?.message}
                    $subtitleTextColor={theme.forecast.dangerText}
                    onChange={(e) => {
                      fieldProps.onChange(e.target.value)
                      formTrigger(fieldProps.name)
                    }}
                    $borderColor={borderColor}
                    $labelColor={labelColor}
                    $label={formItemLabel}
                    $labelLocation={
                      formItemLabel !== undefined ? 'side' : undefined
                    }
                    mode={fieldMode}
                    disabled={formItem.disabled}
                    type='input'
                    {...labelledByProps}
                  />
                </StyledFormItem>
              )
          }
        }}
      />
    </>
  )
}
